import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePageParam } from "@/hooks/use-page-param";
import { ListPagination } from "@/components/ui/list-pagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Plus } from "lucide-react";
import { ProjetoForm } from "@/components/projetos/ProjetoForm";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateBR } from "@/utils/dateUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FreelancersVencimentoCard } from "@/components/projetos/FreelancersVencimentoCard";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const STATUS_OPTIONS = [
  { value: "em_andamento", label: "Em Andamento", color: "bg-blue-500" },
  { value: "concluido", label: "Concluído", color: "bg-green-500" },
  { value: "atrasado", label: "Atrasado", color: "bg-red-500" },
];

export default function Projetos() {
  const [showForm, setShowForm] = useState(false);
  const [areaFiltro, setAreaFiltro] = useState<string | null>(null);
  const [statusFiltro, setStatusFiltro] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: areas } = useQuery({
    queryKey: ["areas_atuacao", "active", "all-columns"],
    queryFn: async () => {
      const { data } = await supabase.from("areas_atuacao").select("*").eq("is_active", true);
      return data;
    },
  });

  // Key SEM os filtros: eles são aplicados client-side (useMemo abaixo). Antes
  // areaFiltro/statusFiltro/searchTerm faziam parte da queryKey, então CADA
  // mudança de filtro/busca refazia o fetch da tabela inteira do servidor,
  // mesmo a filtragem sendo em memória. Agora é 1 fetch só, filtrado localmente.
  const { data: projetosRaw, refetch } = useQuery({
    queryKey: ["projetos"],
    queryFn: async () => {
      const { data } = await supabase
        .from("projetos")
        .select(
          `
          *,
          freelancer:freelancers(nome, areas, pretensao_salarial),
          agencia:agencias(nome),
          area:areas_atuacao(nome)
        `
        )
        .order("created_at", { ascending: false });

      return data ?? [];
    },
  });

  const projetos = useMemo(() => {
    return (projetosRaw ?? []).filter((projeto) => {
      if (areaFiltro && projeto.area_atuacao_id !== areaFiltro) {
        return false;
      }

      if (statusFiltro && projeto.status !== statusFiltro) {
        return false;
      }

      if (!searchTerm) return true;

      const term = searchTerm.toLowerCase();
      return (
        projeto.status?.toLowerCase().includes(term) ||
        projeto.freelancer?.nome?.toLowerCase().includes(term) ||
        projeto.area?.nome?.toLowerCase().includes(term) ||
        projeto.agencia?.nome?.toLowerCase().includes(term)
      );
    });
  }, [projetosRaw, areaFiltro, statusFiltro, searchTerm]);

  const [currentPage, setCurrentPage] = usePageParam();
  const PAGE_SIZE = 30;
  const totalPages = Math.max(1, Math.ceil(projetos.length / PAGE_SIZE));
  const paginaEfetiva = Math.min(currentPage, totalPages);
  const projetosPagina = projetos.slice(
    (paginaEfetiva - 1) * PAGE_SIZE,
    paginaEfetiva * PAGE_SIZE,
  );

  const atualizarStatusProjeto = async (projetoId: string, novoStatus: string) => {
    const { error } = await supabase
      .from("projetos")
      .update({ status: novoStatus })
      .eq("id", projetoId);

    if (error) {
      console.error("Erro ao atualizar status:", error);
      return;
    }

    refetch();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projetos"
        subtitle="Gerencie os projetos em andamento"
        actions={
          <Button onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" /> Novo Projeto
          </Button>
        }
      />

      <FilterToolbar>
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Buscar por freelancer, área, agência..."
        />
        <Select
          value={statusFiltro || "todos"}
          onValueChange={(value) => setStatusFiltro(value === "todos" ? null : value)}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            {STATUS_OPTIONS.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={areaFiltro || "todos"}
          onValueChange={(value) => setAreaFiltro(value === "todos" ? null : value)}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Área" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas as áreas</SelectItem>
            {areas?.map((area) => (
              <SelectItem key={area.id} value={area.id}>
                {area.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterToolbar>

      <div className="grid gap-4 md:grid-cols-2">
        <FreelancersVencimentoCard projetos={projetosRaw ?? []} />

        <Card>
          <CardHeader>
            <CardTitle>Resumo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total de Projetos
                </p>
                <p className="text-2xl font-bold">{projetos?.length || 0}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Em Andamento
                </p>
                <p className="text-2xl font-bold">
                  {projetos?.filter((p) => p.status === "em_andamento").length || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <section className="space-y-3">
        <div className="flex items-center gap-2 border-b pb-2">
          <h2 className="text-lg font-semibold">Projetos em Andamento</h2>
        </div>
        <div className="overflow-x-auto rounded-md border">
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Freelancer</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Pretensão Salarial</TableHead>
                  <TableHead>Agência</TableHead>
                  <TableHead>Data Início</TableHead>
                  <TableHead>Data Conclusão</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projetosPagina.map((projeto) => (
                  <TableRow key={projeto.id}>
                    <TableCell>{projeto.freelancer?.nome}</TableCell>
                    <TableCell>{projeto.area?.nome}</TableCell>
                    <TableCell>{projeto.freelancer?.pretensao_salarial}</TableCell>
                    <TableCell>{projeto.agencia?.nome}</TableCell>
                    <TableCell>
                      {projeto.data_inicio ? formatDateBR(projeto.data_inicio) : "-"}
                    </TableCell>
                    <TableCell>
                      {projeto.data_conclusao ? formatDateBR(projeto.data_conclusao) : "-"}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={projeto.status || ""}
                        onValueChange={(value) => atualizarStatusProjeto(projeto.id, value)}
                      >
                        <SelectTrigger className="w-[180px] border-0 bg-transparent p-0 hover:bg-transparent focus:ring-0">
                          <SelectValue>
                            {projeto.status && (
                              <span
                                className={`px-2 py-1 rounded-full text-white text-sm ${
                                  STATUS_OPTIONS.find(
                                    (status) => status.value === projeto.status
                                  )?.color || "bg-gray-500"
                                }`}
                              >
                                {STATUS_OPTIONS.find(
                                  (status) => status.value === projeto.status
                                )?.label || projeto.status}
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((status) => (
                            <SelectItem
                              key={status.value}
                              value={status.value}
                              className="focus:bg-transparent"
                            >
                              <span
                                className={`px-2 py-1 rounded-full text-white text-sm ${status.color}`}
                              >
                                {status.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <ListPagination
              className="mt-3"
              currentPage={paginaEfetiva}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </section>

      <Sheet open={showForm} onOpenChange={setShowForm}>
        <SheetContent className="sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Novo Projeto</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <ProjetoForm 
              onSuccess={() => setShowForm(false)}
              onCancel={() => setShowForm(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
